import { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, Maximize2, Eraser, Minimize2 } from 'lucide-react';
import type { GenerationMode } from './types';
import toast from '../../components/Toast';
import { compressImageFiles } from '../../utils/imageCompress';
import { withAuthToken } from '../../utils/storageUrl';

const MAX_REFERENCE_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_MIME = /^image\/(png|jpe?g|webp|gif|bmp)$/i;

interface ReferenceImageUploaderProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  mode: GenerationMode;
  maxReferenceImages?: number;
  maskImage?: string;
  onMaskChange?: (mask: string) => void;
}

export function ReferenceImageUploader({
  images,
  onImagesChange,
  mode,
  maxReferenceImages = 16,
  maskImage,
  onMaskChange
}: ReferenceImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fullscreenCanvasRef = useRef<HTMLCanvasElement>(null);
  const [, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [showMaskEditor, setShowMaskEditor] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const sourceImageRef = useRef<HTMLImageElement | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const maxImages = mode === 'img2img' ? maxReferenceImages : 1;
    const remainingSlots = maxImages - images.length;

    // 文件类型 / 大小过滤（在分发到 FileReader 之前，避免大文件吃 base64 内存）
    const accepted: File[] = [];
    let rejectedTooLarge = 0;
    let rejectedWrongType = 0;
    for (const f of Array.from(files).slice(0, remainingSlots)) {
      if (!ACCEPTED_IMAGE_MIME.test(f.type)) {
        rejectedWrongType++;
        continue;
      }
      if (f.size > MAX_REFERENCE_IMAGE_BYTES) {
        rejectedTooLarge++;
        continue;
      }
      accepted.push(f);
    }
    if (rejectedTooLarge > 0) {
      toast.error(
        `${rejectedTooLarge} 张图片超过 ${Math.floor(MAX_REFERENCE_IMAGE_BYTES / 1024 / 1024)}MB 已跳过`
      );
    }
    if (rejectedWrongType > 0) {
      toast.error(`${rejectedWrongType} 张文件不是支持的图片格式（png/jpg/webp/gif/bmp）`);
    }
    if (accepted.length === 0) {
      // 清空 input value，方便用户重新选同一张图（修正大小后）
      e.target.value = '';
      return;
    }

    // img2img 模式：压缩参考图减少传输体积；inpaint 模式：保持原图质量
    const readFiles = mode === 'img2img'
      ? compressImageFiles(accepted)
      : Promise.all(
          accepted.map(
            (file) =>
              new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                  if (event.target?.result) {
                    resolve(event.target.result as string);
                  }
                };
                reader.readAsDataURL(file);
              })
          )
        );

    readFiles.then((newImages) => {
      onImagesChange([...images, ...newImages].slice(0, maxImages));
    });
    // 清空 input.value，避免选同一张图不触发 change
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    onImagesChange(images.filter((_, i) => i !== index));
    if (mode === 'inpaint' && images.length === 1) {
      onMaskChange?.('');
    }
  };

  useEffect(() => {
    if (mode === 'inpaint' && images.length > 0 && showMaskEditor && canvasRef.current && !isFullscreen) {
      initCanvas(canvasRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, images[0], showMaskEditor, isFullscreen]);

  useEffect(() => {
    sourceImageRef.current = null;
    maskCanvasRef.current = null;
    onMaskChange?.('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, images[0]]);

  useEffect(() => {
    if (isFullscreen && fullscreenCanvasRef.current && images.length > 0) {
      initCanvas(fullscreenCanvasRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen, images[0]]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const initCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const existingImage = sourceImageRef.current;
    if (existingImage && existingImage.src === images[0]) {
      canvas.width = existingImage.width;
      canvas.height = existingImage.height;
      renderCanvas(canvas);
      return;
    }

    const img = new Image();
    img.onload = () => {
      sourceImageRef.current = img;
      canvas.width = img.width;
      canvas.height = img.height;
      ensureMaskCanvas(img.width, img.height);
      renderCanvas(canvas);
    };
    img.src = images[0];
  };

  const ensureMaskCanvas = (width: number, height: number) => {
    if (!maskCanvasRef.current) {
      maskCanvasRef.current = document.createElement('canvas');
    }

    const maskCanvas = maskCanvasRef.current;
    if (maskCanvas.width !== width || maskCanvas.height !== height) {
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskCtx = maskCanvas.getContext('2d');
      if (maskCtx) {
        maskCtx.clearRect(0, 0, width, height);
      }
    }
  };

  const renderCanvas = (canvas: HTMLCanvasElement | null) => {
    if (!canvas || !sourceImageRef.current) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (canvas.width !== sourceImageRef.current.width || canvas.height !== sourceImageRef.current.height) {
      canvas.width = sourceImageRef.current.width;
      canvas.height = sourceImageRef.current.height;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(sourceImageRef.current, 0, 0);

    if (maskCanvasRef.current) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.drawImage(maskCanvasRef.current, 0, 0);
      ctx.restore();
    }
  };

  const syncCanvases = () => {
    renderCanvas(canvasRef.current);
    renderCanvas(fullscreenCanvasRef.current);
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    isDrawingRef.current = true;
    draw(e, isFullscreen);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    isDrawingRef.current = false;

    if (maskCanvasRef.current && sourceImageRef.current && onMaskChange) {
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = sourceImageRef.current.width;
      maskCanvas.height = sourceImageRef.current.height;
      const maskCtx = maskCanvas.getContext('2d');

      if (maskCtx) {
        maskCtx.fillStyle = 'rgba(0, 0, 0, 1)';
        maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
        maskCtx.globalCompositeOperation = 'destination-out';
        maskCtx.drawImage(maskCanvasRef.current, 0, 0);
        maskCtx.globalCompositeOperation = 'source-over';
        onMaskChange(maskCanvas.toDataURL('image/png'));
      }
    }

    syncCanvases();
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>, isFullscreenMode = false) => {
    if (!isDrawingRef.current) return;
    const canvas = isFullscreenMode ? fullscreenCanvasRef.current : canvasRef.current;
    if (!canvas || !sourceImageRef.current) return;

    ensureMaskCanvas(sourceImageRef.current.width, sourceImageRef.current.height);
    const maskCanvas = maskCanvasRef.current;
    const maskCtx = maskCanvas?.getContext('2d');
    if (!maskCanvas || !maskCtx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (maskCanvas.width / rect.width);
    const y = (e.clientY - rect.top) * (maskCanvas.height / rect.height);

    maskCtx.fillStyle = '#3b82f6';
    maskCtx.beginPath();
    maskCtx.arc(x, y, brushSize, 0, Math.PI * 2);
    maskCtx.fill();

    syncCanvases();
  };

  const clearMask = () => {
    const maskCanvas = maskCanvasRef.current;
    const maskCtx = maskCanvas?.getContext('2d');
    if (maskCanvas && maskCtx) {
      maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
    }

    syncCanvases();
    onMaskChange?.('');
  };

  if (mode === 'text2img') {
    return null;
  }

  const maxImages = mode === 'img2img' ? maxReferenceImages : 1;
  const label = mode === 'img2img' ? '参考图片' : '原图（需要绘制蒙版）';

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-light-text">{label}</label>
          <span className="text-xs text-light-muted">
            {images.length}/{maxImages} 张
            {mode === 'img2img' && `（最多${maxReferenceImages}张）`}
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          {images.map((img, index) => (
            <div key={index} className="relative w-24 h-24 rounded-xl overflow-hidden border border-light-border shadow-sm group">
              <img src={withAuthToken(img)} alt={`Reference ${index + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
              {mode === 'inpaint' && index === 0 && (
                <button
                  onClick={() => setShowMaskEditor(!showMaskEditor)}
                  className="absolute bottom-1 left-1 right-1 py-1 bg-accent-blue/80 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {showMaskEditor ? '关闭编辑' : '编辑蒙版'}
                </button>
              )}
            </div>
          ))}

          {images.length < maxImages && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-24 h-24 rounded-xl border-2 border-dashed border-light-border hover:border-accent-blue flex flex-col items-center justify-center gap-1 transition-all hover:shadow-md hover:scale-105"
            >
              <Upload className="w-5 h-5 text-light-muted" />
              <span className="text-xs text-light-muted">上传</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={mode === 'img2img'}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>

      {mode === 'inpaint' && images.length > 0 && showMaskEditor && (
        <div className="border border-light-border rounded-xl p-4 bg-light-bg">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-light-text">蒙版编辑</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-light-muted">画笔大小:</span>
              <input
                type="range"
                min="5"
                max="50"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-20"
              />
              <button
                onClick={() => setIsFullscreen(true)}
                className="p-1.5 text-light-muted hover:text-accent-blue transition-colors"
                title="全屏编辑"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                onClick={clearMask}
                className="p-1.5 text-light-muted hover:text-red-500 transition-colors"
                title="清除蒙版"
              >
                <Eraser className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="relative overflow-auto max-h-[400px] border border-light-border rounded-lg">
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={(e) => draw(e, false)}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              className="cursor-crosshair max-w-full"
              style={{ display: 'block' }}
            />
          </div>
          <p className="text-xs text-light-muted mt-2">
            在图片上涂抹需要重绘的区域，透明区域将被重新生成
          </p>
        </div>
      )}

      {isFullscreen && (
        <div className="fixed inset-0 z-[10000] bg-black/90 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700">
            <div className="flex items-center gap-4">
              <span className="text-white font-medium">蒙版全屏编辑</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">画笔大小:</span>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-32"
                />
                <span className="text-xs text-white w-8">{brushSize}px</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearMask}
                className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
              >
                <Eraser className="w-4 h-4" />
                清除
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className="px-3 py-1.5 text-sm bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors flex items-center gap-1"
              >
                <Minimize2 className="w-4 h-4" />
                退出全屏
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
            <canvas
              ref={fullscreenCanvasRef}
              onMouseDown={startDrawing}
              onMouseMove={(e) => draw(e, true)}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              className="cursor-crosshair max-w-full max-h-full object-contain"
              style={{ display: 'block' }}
            />
          </div>

          <div className="px-4 py-2 bg-gray-900 border-t border-gray-700 text-center">
            <span className="text-xs text-gray-400">
              在图片上涂抹需要重绘的区域，透明区域将被重新生成 | 点击退出全屏按钮或按 ESC 键退出
            </span>
          </div>
        </div>
      )}

      {mode === 'inpaint' && images.length > 0 && !showMaskEditor && !maskImage && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-600" />
          <span className="text-sm text-yellow-700">请点击图片上的"编辑蒙版"按钮绘制重绘区域</span>
        </div>
      )}
    </div>
  );
}
